import React, { FC, useEffect, useMemo, useState } from "react";
import { usePageStore } from "@/hooks/usePageStore";
import { Box } from "@mui/material";
import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import { useCreateAuditlog, useGetAuditLogsAll } from "@/hooks/useAuditLogs";
import { useUserStore } from "@/hooks/useUserStore";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import Select from "@/components/Select";
import BasicDatepicker from "@/components/Datepicker";
import dayjs, { Dayjs } from "dayjs";
import { headCellsConfig } from "@/utils/tableConfig";
import { useGetSettings } from "@/hooks/useSetting";
import Breadcrumbs from "@/components/Breadcrumbs";

type Ordering = {
  pageNumber: number;
  pageSize: number;
  sortOrder: number;
  sortField: string;
};

const AuditLogsPage: FC = () => {
  const { setPageTitle } = usePageStore();
  const { userInfo } = useUserStore();
  const { data: settings } = useGetSettings();

  const createAuditLog = useCreateAuditlog();
  const auditLogViewCalledRef = React.useRef(false);

  useEffect(() => {
    setPageTitle("Audit Logs");
  }, []);

  // Estado de filtros
  const [filters, setFilters] = useState({
    module: "Users", // Default: Módulo de Usuarios
    dateFrom: dayjs().subtract(1, "month"), // Default: Hace 1 mes
    dateTo: dayjs(), // Default: Fecha actual (hoy)
  });

  const [sortOrdering, setSortOrdering] = useState<Ordering>({
    pageNumber: 0,
    pageSize: 25,
    sortOrder: -1,
    sortField: "timestamp",
  });

  // Consultar audit logs
  const {
    data: auditLogs,
    refetch: refetchAuditLogs,
    isFetching,
  } = useGetAuditLogsAll({
    module: filters.module === "All Modules" ? "" : filters.module,
    from: filters.dateFrom.startOf("day").toISOString(),
    to: filters.dateTo.endOf("day").toISOString(),
    pageNumber: sortOrdering.pageNumber,
    pageSize: sortOrdering.pageSize,
    sortField: sortOrdering.sortField,
    sortOrder: sortOrdering.sortOrder,
  });

  const headCells: HeadCell[] = headCellsConfig.auditLog;

  // Crear filas de la tabla
  const rows = useMemo(() => {
    if (!auditLogs?.data) {
      return [];
    }

    const cellStyle = { fontSize: "14px" };

    return auditLogs.data.map(
      (
        row: {
          timestamp: string;
          user: string;
          eventDescription: string;
        },
        index: number,
      ) => {
        return createDataRow(index, {
          timestamp: (
            <div style={{ ...cellStyle, minWidth: "210px" }}>
              {dayjs(row.timestamp).format(
                (settings?.generalDateFormat as string) ||
                  "DD MMM YYYY, hh:mm A",
              )}
            </div>
          ),
          user: (
            <div style={{ ...cellStyle, minWidth: "180px" }}>{row.user}</div>
          ),
          eventDescription: (
            <div style={{ ...cellStyle, minWidth: "350px" }}>
              {row.eventDescription}
            </div>
          ),
        });
      },
    );
  }, [auditLogs, settings?.generalDateFormat]);

  // Refetch cuando cambian los filtros u ordenamiento
  useEffect(() => {
    if (filters.dateFrom && filters.dateTo) {
      refetchAuditLogs();
    }
  }, [
    filters.module,
    filters.dateFrom,
    filters.dateTo,
    sortOrdering.pageNumber,
    sortOrdering.pageSize,
    sortOrdering.sortField,
    sortOrdering.sortOrder,
  ]);

  // Registrar vista de la página en auditlog
  useEffect(() => {
    if (userInfo && !auditLogViewCalledRef.current) {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.GENERAL,
        option: OPTIONS.AUDIT_LOG_VIEW,
        actionCode: ACTION_CODE.WEB_GEN_LIST_AUDITLOG_VIEW,
        action: "When entering",
        detail: "Viewed the audit logs page",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
        name: `${userInfo?.firstName} ${userInfo?.lastName}`,
      });
      auditLogViewCalledRef.current = true;
    }
  }, [userInfo, createAuditLog]);

  const handleModuleChange = (value: string | string[]) => {
    setFilters((prev) => ({
      ...prev,
      module: value as string,
    }));
    setSortOrdering((prev) => ({ ...prev, pageNumber: 0 }));
  };

  const handleDateFromChange = (value: Dayjs | null) => {
    if (value) {
      setFilters((prev) => ({
        ...prev,
        dateFrom: value,
      }));
      setSortOrdering((prev) => ({ ...prev, pageNumber: 0 }));
    }
  };

  const handleDateToChange = (value: Dayjs | null) => {
    if (value) {
      setFilters((prev) => ({
        ...prev,
        dateTo: value,
      }));
      setSortOrdering((prev) => ({ ...prev, pageNumber: 0 }));
    }
  };

  return (
    <Box>
      <Breadcrumbs
        items={[
          {
            label: "Audit Logs",
            href: "/auditlogs",
            isLink: false,
          },
        ]}
      />

      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          mt: 2,
          alignItems: "center",
          flexWrap: "nowrap",
        }}
      >
        {/* Filtro de Módulo */}
        <Select
          id="module-filter"
          label="Module"
          value={filters.module}
          handleChange={handleModuleChange}
          data={[
            { key: "All Modules", value: "All Modules" },
            ...Object.values(MODULE_NAME).map((moduleName) => ({
              key: moduleName,
              value: moduleName,
            })),
          ]}
          sx={{ minWidth: 220 }}
        />

        {/* Filtro de Fecha FROM */}
        <BasicDatepicker
          label="Date From"
          value={filters.dateFrom}
          onChange={handleDateFromChange}
          maxDate={filters.dateTo}
          sx={{
            minWidth: 200,
            "& .MuiInputBase-root": { height: 48, maxHeight: 48 },
            "& input": {
              height: 48,
              maxHeight: 48,
              padding: "12px 14px",
            },
          }}
          disableWrite={true}
        />

        {/* Filtro de Fecha TO */}
        <BasicDatepicker
          label="Date To"
          value={filters.dateTo}
          onChange={handleDateToChange}
          minDate={filters.dateFrom}
          maxDate={dayjs()} // No se puede seleccionar fecha futura
          sx={{
            minWidth: 200,
            "& .MuiInputBase-root": { height: 48, maxHeight: 48 },
            "& input": {
              height: 48,
              maxHeight: 48,
              padding: "12px 14px",
            },
          }}
          disableWrite={true}
        />
      </Box>

      {/* Tabla de Audit Logs */}
      <DataTable
        data={rows}
        headCells={headCells}
        order={sortOrdering.sortOrder === 1 ? "asc" : "desc"}
        orderBy={sortOrdering.sortField}
        rowsPerPage={sortOrdering.pageSize}
        page={sortOrdering.pageNumber}
        total={auditLogs?.total || 0}
        isLoading={isFetching}
        onChangePage={(event, newPage) => {
          setSortOrdering((prev) => ({
            ...prev,
            pageNumber: newPage,
          }));
        }}
        onChangeSize={(event) => {
          setSortOrdering((prev) => ({
            ...prev,
            pageNumber: 0,
            pageSize: Number(event.target.value),
          }));
        }}
        onChangeSort={(order, orderBy) => {
          setSortOrdering((prev) => ({
            ...prev,
            pageNumber: 0,
            sortField: orderBy as string,
            sortOrder: order === "asc" ? 1 : -1,
          }));
        }}
        messageEmptyData="No audit logs found for the selected filters."
      />
    </Box>
  );
};

export default AuditLogsPage;
